// soloDetect.ts — auto-label a separated clip by its "solo signature" from the
// per-stem RMS energies. Ported verbatim (thresholds and all) from the Riffloop
// extension's content.js (Moshe's classifier, calibrated 2025-07 against real
// field cases). The extension runs this on the whole song; the Studio runs it
// on whatever region the user separated, so it typically labels one clip.
import type { StemName } from './studioEngine';

export interface SoloRegion {
  label: string;
  startSec: number;
  endSec: number;
}

type StemBuffers = Partial<Record<StemName, [Float32Array, Float32Array]>>;
type Energy = Partial<Record<StemName, number>>;
type EnergySegs = Partial<Record<StemName, number[]>>;

// Below EMPTY: a stem is considered silent (post-isolation swap + hints).
const EMPTY_ENERGY_THRESHOLD = 0.015;
// At or above AUDIBLE: a stem is unambiguously present/carrying content.
const AUDIBLE_ENERGY_THRESHOLD = 0.03;

// RMS over the whole stem (stride-subsampled — RMS is robust to it).
function floatRms(left: Float32Array, right?: Float32Array): number {
  const n = left ? left.length : 0;
  if (!n) return 0;
  let sumSq = 0;
  let count = 0;
  for (let i = 0; i < n; i += 4) {
    const a = left[i] || 0;
    sumSq += a * a;
    count++;
    if (right && right !== left) {
      const b = right[i] || 0;
      sumSq += b * b;
      count++;
    }
  }
  return count ? Math.sqrt(sumSq / count) : 0;
}

// ~2s of audio per slice (clamped 8..48) so detected boundaries land within a
// couple of seconds of the real ones.
const ENERGY_SEG_TARGET_SECONDS = 2;
function energySegCount(secs: number): number {
  return Math.max(8, Math.min(48, Math.round((secs || 0) / ENERGY_SEG_TARGET_SECONDS) || 8));
}
// RMS per time-slice, so the detector can see WITHIN the clip.
function floatRmsSegs(left: Float32Array, right: Float32Array | undefined, sampleRate: number): number[] | null {
  const n = left ? left.length : 0;
  if (!n) return null;
  const P = energySegCount(n / (sampleRate || 44100));
  const out: number[] = [];
  for (let p = 0; p < P; p++) {
    const a = Math.floor((n * p) / P);
    const b = Math.floor((n * (p + 1)) / P);
    let sumSq = 0;
    let count = 0;
    for (let i = a; i < b; i += 4) {
      const x = left[i] || 0;
      sumSq += x * x;
      count++;
      if (right && right !== left) {
        const y = right[i] || 0;
        sumSq += y * y;
        count++;
      }
    }
    out.push(count ? Math.sqrt(sumSq / count) : 0);
  }
  return out;
}

// Whole-window signature → a solo label or null. Four strict rules.
function detectSoloOnWindow(energy: Energy): string | null {
  if (!energy) return null;
  const e = (k: StemName) => +(energy[k] as number) || 0;
  const quiet = (k: StemName, lead: number, frac: number) =>
    e(k) <= Math.max(EMPTY_ENERGY_THRESHOLD, frac * lead);

  // 1) Melodic lead solo (guitar / piano / other), vocals absent, lead dominant.
  const LEADS: { stem: StemName; label: string }[] = [
    { stem: 'guitar', label: 'Guitar solo' },
    { stem: 'piano', label: 'Piano solo' },
    { stem: 'other', label: 'Solo' },
  ];
  let lead: { stem: StemName; label: string; e: number } | null = null;
  for (const c of LEADS) {
    if (!lead || e(c.stem) > lead.e) lead = { ...c, e: e(c.stem) };
  }
  if (lead && lead.e >= AUDIBLE_ENERGY_THRESHOLD && quiet('vocals', lead.e, 0.25)) {
    let dominant = true;
    for (const c of LEADS) {
      if (c.stem !== lead.stem && e(c.stem) > lead.e / 1.4) {
        dominant = false;
        break;
      }
    }
    if (dominant) return lead.label;
  }
  // 2) Bass solo (drums may accompany, but not dwarf it).
  const bass = e('bass');
  if (
    bass >= AUDIBLE_ENERGY_THRESHOLD &&
    quiet('vocals', bass, 0.25) &&
    e('drums') <= 1.2 * bass &&
    quiet('guitar', bass, 0.5) &&
    quiet('piano', bass, 0.5) &&
    quiet('other', bass, 0.5)
  ) {
    return 'Bass solo';
  }
  // 3) Drum solo — everything else quiet.
  const drums = e('drums');
  if (
    drums >= AUDIBLE_ENERGY_THRESHOLD &&
    quiet('vocals', drums, 0.2) &&
    quiet('bass', drums, 0.2) &&
    quiet('guitar', drums, 0.2) &&
    quiet('piano', drums, 0.2) &&
    quiet('other', drums, 0.2)
  ) {
    return 'Drum solo';
  }
  // 4) Vocal solo / a cappella — 0.35 tolerates soft comping under the voice.
  const vocals = e('vocals');
  if (
    vocals >= AUDIBLE_ENERGY_THRESHOLD &&
    quiet('drums', vocals, 0.35) &&
    quiet('bass', vocals, 0.35) &&
    quiet('guitar', vocals, 0.35) &&
    quiet('piano', vocals, 0.35) &&
    quiet('other', vocals, 0.35)
  ) {
    return 'Vocal solo';
  }
  return null;
}

const SOLO_MIN_SECONDS = 10;
const SOLO_MAX_REGIONS = 3;

// Classify every slice, group contiguous same-label runs ≥ SOLO_MIN_SECONDS,
// keep the SOLO_MAX_REGIONS longest. Returns 0..1 window fractions.
function detectSoloRegions(
  energy: Energy,
  energySegs: EnergySegs | null,
  winLen: number,
): { label: string; startFrac: number; endFrac: number }[] {
  const stems = energySegs ? (Object.keys(energySegs) as StemName[]) : [];
  const segCount = stems.length ? (energySegs![stems[0]] || []).length : 0;
  if (!energySegs || segCount < 4 || !(winLen > 0)) {
    const whole = detectSoloOnWindow(energy);
    return whole ? [{ label: whole, startFrac: 0, endFrac: 1 }] : [];
  }
  // Per-window stats for the level-independent "elevation" rule.
  const med: Partial<Record<StemName, number>> = {};
  const p90: Partial<Record<StemName, number>> = {};
  for (const s of stems) {
    const sorted = (energySegs[s] || []).slice().sort((a, b) => a - b);
    med[s] = sorted[Math.floor(segCount / 2)] || 0;
    p90[s] = sorted[Math.min(segCount - 1, Math.floor(segCount * 0.9))] || 0;
  }
  const sliceMaxes: number[] = [];
  for (let i = 0; i < segCount; i++) {
    let m = 0;
    for (const s of stems) m = Math.max(m, (energySegs[s] || [])[i] || 0);
    sliceMaxes.push(m);
  }
  const refLoud = sliceMaxes.slice().sort((a, b) => a - b)[Math.floor(segCount / 2)] || 0;
  const ELEV_LEADS: { stem: StemName; label: string }[] = [
    { stem: 'guitar', label: 'Guitar solo' },
    { stem: 'piano', label: 'Piano solo' },
    { stem: 'other', label: 'Solo' },
  ];

  const labels: (string | null)[] = [];
  for (let i = 0; i < segCount; i++) {
    const segEnergy: Energy = {};
    for (const s of stems) segEnergy[s] = (energySegs[s] || [])[i] || 0;
    let L = detectSoloOnWindow(segEnergy);
    if (!L) {
      let lead: { stem: StemName; label: string; e: number } | null = null;
      for (const c of ELEV_LEADS) {
        const e = segEnergy[c.stem] || 0;
        if (!lead || e > lead.e) lead = { ...c, e };
      }
      if (lead) {
        const leadGate = Math.max(0.012, 1.5 * Math.max(med[lead.stem] || 0, 0.25 * refLoud));
        const vocalsGate = Math.max(EMPTY_ENERGY_THRESHOLD, 0.65 * (p90.vocals || 0));
        if (lead.e >= leadGate && (segEnergy.vocals || 0) <= vocalsGate) {
          L = lead.label;
        }
      }
    }
    labels.push(L);
  }
  // Gap tolerance: L, x, L → L, L, L.
  for (let i = 1; i < segCount - 1; i++) {
    if (labels[i] !== labels[i - 1] && labels[i - 1] && labels[i - 1] === labels[i + 1]) {
      labels[i] = labels[i - 1];
    }
  }
  const sliceSec = winLen / segCount;
  const minSlices = Math.max(2, Math.round(SOLO_MIN_SECONDS / sliceSec));
  const runs: { label: string; start: number; len: number }[] = [];
  let cur: string | null = null;
  let curStart = 0;
  let curLen = 0;
  for (let i = 0; i <= segCount; i++) {
    const L = i < segCount ? labels[i] : null;
    if (L && L === cur) {
      curLen++;
      continue;
    }
    if (cur && curLen >= minSlices) runs.push({ label: cur, start: curStart, len: curLen });
    cur = L;
    curStart = i;
    curLen = L ? 1 : 0;
  }
  if (!runs.length) {
    const whole = detectSoloOnWindow(energy);
    return whole ? [{ label: whole, startFrac: 0, endFrac: 1 }] : [];
  }
  runs.sort((a, b) => b.len - a.len);
  const kept = runs.slice(0, SOLO_MAX_REGIONS);
  kept.sort((a, b) => a.start - b.start);
  return kept.map((r) => ({
    label: r.label,
    startFrac: r.start / segCount,
    endFrac: (r.start + r.len) / segCount,
  }));
}

/**
 * Analyze the separated stem PCM and return any solo regions (absolute seconds,
 * relative to the separated clip). Cheap — a stride-subsampled RMS pass.
 */
export function detectSolos(stems: StemBuffers, sampleRate: number): SoloRegion[] {
  const names = Object.keys(stems) as StemName[];
  if (!names.length) return [];
  const energy: Energy = {};
  const segs: EnergySegs = {};
  let durationSec = 0;
  for (const name of names) {
    const pair = stems[name];
    if (!pair) continue;
    const [left, right] = pair;
    energy[name] = floatRms(left, right);
    const s = floatRmsSegs(left, right, sampleRate);
    if (s) segs[name] = s;
    durationSec = Math.max(durationSec, (left?.length || 0) / (sampleRate || 44100));
  }
  if (!(durationSec > 0)) return [];
  const regions = detectSoloRegions(energy, Object.keys(segs).length ? segs : null, durationSec);
  return regions.map((r) => ({
    label: r.label,
    startSec: r.startFrac * durationSec,
    endSec: r.endFrac * durationSec,
  }));
}

// Emoji + stem color for a detected label (for chips/bands).
export function soloGlyph(label: string): string {
  const l = label.toLowerCase();
  if (l.includes('guitar')) return '🎸';
  if (l.includes('piano')) return '🎹';
  if (l.includes('drum')) return '🥁';
  if (l.includes('bass')) return '🎸';
  if (l.includes('vocal')) return '🎤';
  return '✨';
}
export function soloStem(label: string): StemName {
  const l = label.toLowerCase();
  if (l.includes('guitar')) return 'guitar';
  if (l.includes('piano')) return 'piano';
  if (l.includes('drum')) return 'drums';
  if (l.includes('bass')) return 'bass';
  if (l.includes('vocal')) return 'vocals';
  return 'other';
}
